import { Component, EventEmitter, Output, ElementRef, HostListener } from '@angular/core';
import { IModule } from 'src/app/models/module.model';
import { MENU_V2, MenuV2Item, MenuV2Section, generateModuleMenuItems } from '../../menu-v2.config';
import { ICONS } from '../../icons';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { IconComponent } from '../../icon/icon.component';
import { AuthService } from 'src/app/services/auth.service';
import { HyperDxService } from 'src/app/services/hyperdx.service';
import { IntercomService } from 'src/app/services/intercom.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { hasTreatmentPlanAccess } from 'src/app/utils/user-permissions.utils';
import { ClinicSwitcherComponent } from 'src/app/components/clinic-switcher/clinic-switcher.component';
import { ContextService } from 'src/app/services/context.service';
import { IClinicContext } from 'src/app/models/clinic-context.model';

@Component({
  selector: 'app-sidebar-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    IconComponent,
    ClinicSwitcherComponent
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.sass']
})
export class SidebarComponent {
  @Output() closeMobileSidebar = new EventEmitter<void>();
  
  modules: IModule[] = [];
  selectedModule: IModule = { id: '', description: '' };
  menu: MenuV2Section[] = [];
  readonly ICONS = ICONS;
  username = "";
  email = "";
  companyName = "";
  logo = "";
  isCollapsed = false;
  loggingOut = false;
  permission = '';
  // Control for custom module dropdown
  moduleDropdownOpen = false;
  // ROOT: tracks whether a clinic is currently active
  rootHasActiveClinic = false;
  private originalCompanyName = '';
  private activeClinicSub?: Subscription;

  constructor(
    private authService: AuthService,
    private hyperDxService: HyperDxService,
    private intercomService: IntercomService,
    private router: Router,
    private elementRef: ElementRef,
    private contextService: ContextService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Close module dropdown when clicking outside the sidebar
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.moduleDropdownOpen = false;
    }
  }

  onCloseMobileSidebar(): void {
    this.closeMobileSidebar.emit();
  }

  // ngOnInit duplicado removido (mantido apenas o que implementa as regras do menu antigo)

  // Função para comparar módulos no select (usada pelo compareWith)
  compareModules(m1: IModule, m2: IModule): boolean {
    return m1 && m2 ? m1.id === m2.id : m1 === m2;
  }

  // Handler para mudança de módulo via ngModel
  onModuleSelectChange(module: IModule) {
    if (module) {
      this.authService.setSelectedModule(module);
      this.buildDynamicMenu();
      this.router.navigate(['/main/v2']);
    }
  }

  onModuleChange(event: any) {
    const moduleId = event.target.value;
    const found = this.modules.find(m => m.id === moduleId);
    if (found) {
      this.selectedModule = found;
      this.authService.setSelectedModule(found);
      this.buildDynamicMenu(); // Rebuild menu when module changes
      this.router.navigate(['/main/v2']); // Redirect to main/v2
    }
  }

  toggleModuleDropdown(): void {
    this.moduleDropdownOpen = !this.moduleDropdownOpen;
  }

  selectModule(module: IModule): void {
    if (!module) return;
    this.selectedModule = module;
    this.authService.setSelectedModule(module);
    this.buildDynamicMenu();
    this.moduleDropdownOpen = false;
    this.router.navigate(['/main/v2']);
  }

  /**
   * Constrói o menu dinamicamente baseado nos módulos do usuário
   */
  private buildDynamicMenu(): void {
    // Deep clone do menu base
    this.menu = JSON.parse(JSON.stringify(MENU_V2));
    
    // Encontra a seção de operação
    const operationSection = this.menu.find(s => s.title === 'operação');
    if (!operationSection) return;
    
    // Remove o marcador de módulos dinâmicos
    operationSection.items = operationSection.items.filter(item => !item.isDynamicModule);
    
    // Módulos que já são tratados como itens fixos (não gerar dinâmicamente)
    // Nota: PRESTADORES aqui se refere ao módulo de operação (prestadores como clientes),
    // não ao item "Prestadores" da administração (cadastro de profissionais)
    const fixedModules = ['PACIENTES', 'USUARIOS'];
    
    // Adiciona os itens de módulos dinâmicos (todos exceto os que já são fixos)
    this.modules
      .filter(m => m.description && !fixedModules.includes(m.description.toUpperCase()))
      .forEach(module => {
        const moduleItems = generateModuleMenuItems(module.description);
        operationSection.items.push(...moduleItems);
      });
  }

  /**
   * Regras de exibição do menu baseadas no antigo navbar:
   * - Permissão (permissions)
   * - Módulo selecionado (description)
   * - Categoria (ADM/OPERATION)
   * - Condições extras (ex: hasTreatmentPlanAccess, hasToShowAnamnese)
   */
  hasToShowAnamnese = false;
  userId = '';
  clinicCnpj = '';

  ngOnInit(): void {
    this.username = this.authService.getUsername()
      .split(' ').splice(0, 2).join(' ');
    this.email = this.authService.getUserEmail();
    this.companyName = this.authService.getCompanyName();
    this.originalCompanyName = this.companyName;
    this.logo = this.authService.getUserImage();
    this.permission = this.authService.getPermission();
    this.modules = this.authService.getModules();
    this.userId = this.authService.getUserId?.() || '';
    this.clinicCnpj = this.authService.getClinicCnpj?.() || '';
    this.hasToShowAnamnese = ['VIRTUAL', 'BOTH'].includes(this.authService.getAnamneseAuthType?.());

    // Corrige seleção do módulo: busca do storage se possível
    const stored = localStorage.getItem('selectedModule');
    if (stored) {
      try {
        const storedModule = JSON.parse(atob(stored));
        // Verifica se o módulo armazenado ainda está disponível na lista
        const foundModule = this.modules.find(m => m.id === storedModule.id);
        if (foundModule) {
          this.selectedModule = foundModule;
          // Sincroniza com o AuthService
          this.authService.setSelectedModule(foundModule);
        } else {
          this.selectedModule = this.modules[0] || { id: '', description: '' };
          if (this.selectedModule.id) {
            this.authService.setSelectedModule(this.selectedModule);
          }
        }
      } catch {
        this.selectedModule = this.modules[0] || { id: '', description: '' };
        if (this.selectedModule.id) {
          this.authService.setSelectedModule(this.selectedModule);
        }
      }
    } else {
      this.selectedModule = this.modules[0] || { id: '', description: '' };
      if (this.selectedModule.id) {
        this.authService.setSelectedModule(this.selectedModule);
      }
    }
    
    // Constrói o menu dinâmico baseado nos módulos
    this.buildDynamicMenu();

    // Subscribe to active clinic changes — sync modules and clinic context for ALL user types
    this.activeClinicSub = this.contextService.getActiveClinic().subscribe((clinic) => {
      const wasActive = this.rootHasActiveClinic;
      if (this.permission === 'ROOT') {
        this.rootHasActiveClinic = !!clinic?.cnpj;
      }

      // Sincroniza flags e dados da clínica no AuthService (localStorage) para que
      // componentes que leem de forma síncrona no ngOnInit recebam os valores corretos.
      if (clinic?.cnpj) {
        this.clinicCnpj = clinic.cnpj;
        this.authService.setClinicCnpj(clinic.cnpj);
        this.authService.setClinicId(clinic.id);
        this.authService.setCompanyName(clinic.name);
        this.authService.setTreatmentPlanPermission(clinic.hasTreatmentPlans || false);
        this.authService.setCalculatorPermission(clinic.hasCalculator || false);
        this.authService.setAnamneseAuthType(clinic.contractedAnamneseType || 'NOT_CONTRACTED');
        this.authService.setSendDocsType(clinic.signDeliveryType || 'DELIVERY_METHOD_SMS');
        this.authService.setPatientModelPermission(clinic.isModelPatientActive || false);
        if (clinic.logo !== undefined) {
          this.authService.setUserImage(clinic.logo);
          this.logo = clinic.logo;
        }
        this.companyName = clinic.name;
        this.hasToShowAnamnese = ['VIRTUAL', 'BOTH'].includes(clinic.contractedAnamneseType || '');
      }

      if (clinic?.cnpj && clinic?.modules && clinic.modules.length > 0) {
        // Clínica ativa COM módulos: atualiza sidebar
        this.modules = clinic.modules;
        this.authService.setModules(clinic.modules);

        // Mantém o módulo selecionado se ele existir na nova clínica, senão seleciona o primeiro
        const currentModuleStillValid = this.modules.find(m => m.id === this.selectedModule?.id);
        if (currentModuleStillValid) {
          this.selectedModule = currentModuleStillValid;
        } else {
          this.selectedModule = this.modules[0] || { id: '', description: '' };
        }
        if (this.selectedModule.id) {
          this.authService.setSelectedModule(this.selectedModule);
        }
        this.buildDynamicMenu();
      } else if (clinic?.cnpj && Array.isArray(clinic?.modules) && clinic.modules.length === 0) {
        // Clínica ativa com array de módulos vazio (genuinamente sem módulos) — fallback PACIENTES
        this.modules = [{ id: 'default', description: 'PACIENTES' }];
        this.selectedModule = this.modules[0];
        this.authService.setModules(this.modules);
        this.authService.setSelectedModule(this.selectedModule);
        this.buildDynamicMenu();
      } else if (this.permission === 'ROOT' && !clinic?.cnpj && wasActive) {
        // ROOT voltou ao modo ROOT: limpa módulos e restaura nome original
        this.modules = [];
        this.selectedModule = { id: '', description: '' };
        this.companyName = this.originalCompanyName;
        this.authService.setCompanyName(this.originalCompanyName);
        this.buildDynamicMenu();
      }
    });
  }

  ngOnDestroy(): void {
    this.activeClinicSub?.unsubscribe();
  }

  canViewMenuItem(label: string, route: string = '', extra?: any): boolean {
    const moduleDesc = this.selectedModule?.description?.toUpperCase();

    // ROOT with active clinic behaves like ADMIN for operational items
    const rootActsAsAdmin = this.permission === 'ROOT' && this.rootHasActiveClinic;
    const effectivePermissions = rootActsAsAdmin
      ? ['ROOT', 'ADMIN']
      : [this.permission];

    // ROOT com clínica ativa: usa o módulo selecionado da clínica (carregado via subscription)
    const effectiveModule = moduleDesc;

    // Módulos dinâmicos: verifica PRIMEIRO se a rota contém /modules/
    if (route.includes('/modules/')) {
      const routeModuleName = route.split('/modules/')[1]?.split('/')[0]?.toUpperCase();
      const baseCheck = effectivePermissions.some(p => ['ADMIN', 'ATTENDANT'].includes(p)) && effectiveModule === routeModuleName;
      // Formulários (anamnese de módulos dinâmicos) só aparece se a clínica tem anamnese contratada
      if (route.endsWith('/forms')) {
        return baseCheck && this.hasToShowAnamnese;
      }
      return baseCheck;
    }

    // ADM - itens disponíveis em TODOS os módulos (apenas restrição por permissão)
    if (label === 'Empresas') return rootActsAsAdmin || ['ROOT', 'ADMIN'].includes(this.permission);
    // ROOT-only: esconde quando ROOT está atuando como ADMIN de uma clínica
    if (label === 'Templates') return this.permission === 'ROOT' && !this.rootHasActiveClinic;
    if (label === 'Grupos de Clínicas') return this.permission === 'ROOT' && !this.rootHasActiveClinic;
    if (label === 'Intercorrências') return this.permission === 'ROOT' && !this.rootHasActiveClinic;
    if (label === 'Usuários') return effectivePermissions.some(p => ['ADMIN'].includes(p));
    // Dashboard: ROOT puro (sem clínica) vê sempre; com clínica ativa, só no módulo PACIENTES; ADMIN só no módulo PACIENTES
    if (label === 'Dashboard') {
      if (this.permission === 'ROOT' && !this.rootHasActiveClinic) return true;
      return (this.permission === 'ROOT' || ['ADMIN'].includes(this.permission)) && effectiveModule === 'PACIENTES';
    }
    
    // ADM - Prestadores e Procedimentos
    if (label === 'Prestadores' && route === '/main/v2/providers') {
      return effectivePermissions.some(p => ['ADMIN'].includes(p)) && effectiveModule === 'PACIENTES';
    }
    if (label === 'Procedimentos') return effectivePermissions.some(p => ['ADMIN'].includes(p)) && effectiveModule === 'PACIENTES';

    // OPERATION - Módulo PACIENTES
    if (label === 'Pacientes') return effectivePermissions.some(p => ['ADMIN', 'ATTENDANT'].includes(p)) && effectiveModule === 'PACIENTES';
    if (label === 'Anamneses') return effectivePermissions.some(p => ['ADMIN', 'ATTENDANT'].includes(p)) && effectiveModule === 'PACIENTES' && this.hasToShowAnamnese;
    if (label === 'Assinaturas' && route === '/main/v2/signatures') {
      return effectivePermissions.some(p => ['ADMIN', 'ATTENDANT'].includes(p)) && effectiveModule === 'PACIENTES';
    }
    if (label === 'Planos de Tratamento') return effectivePermissions.some(p => ['ADMIN', 'ATTENDANT'].includes(p)) && effectiveModule === 'PACIENTES' && this.hasTreatmentPlanAccessCheck();

    // Compatibilidade com rotas antigas
    if (route.includes('/alunos') || label === 'Alunos') {
      return effectivePermissions.some(p => ['ADMIN', 'ATTENDANT'].includes(p)) && effectiveModule === 'ALUNOS';
    }
    if (route.includes('/terceiros') || label === 'Terceiros') {
      return effectivePermissions.some(p => ['ADMIN', 'ATTENDANT'].includes(p)) && effectiveModule === 'TERCEIROS';
    }

    // If none of the above, fallback
    return true;
  }

  hasTreatmentPlanAccessCheck(): boolean {
    return hasTreatmentPlanAccess(this.userId, this.clinicCnpj);
  }

  /**
   * Verifica se a seção tem pelo menos um item visível
   */
  hasVisibleItems(section: MenuV2Section): boolean {
    return section.items.some(item => this.canViewMenuItem(item.label, item.route));
  }

  logout() {
    this.loggingOut = true;

    // Cria overlay de loading diretamente no body para garantir que fique acima de tudo
    const overlay = document.createElement('div');
    overlay.id = 'logout-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
    overlay.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;">
        <img src="assets/img/Loading.svg" alt="Saindo..." style="width:48px;height:48px;" />
        <span style="margin-top:12px;color:#fff;font-size:14px;font-weight:500;">Saindo...</span>
      </div>
    `;
    document.body.appendChild(overlay);

    setTimeout(() => {
      // Registra o evento de logout
      this.hyperDxService.logAction('user-logout', {
        userId: this.authService.getUserId(),
        userName: this.authService.getUsername(),
        userEmail: this.authService.getUserEmail()
      });

      // Limpa a identificação do usuário no HyperDX
      this.hyperDxService.clearUserIdentification();

      // Encerra o Intercom
      this.intercomService.shutdown();

      // Limpa os dados de autenticação
      this.authService.clear();

      // Redireciona para o novo site padrão
      window.location.href = '/home';
    }, 100);
  }

  @Output() collapsedChange = new EventEmitter<boolean>();
  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    this.collapsedChange.emit(this.isCollapsed);
  }

  /**
   * Retorna o label correto para o item do menu
   * Para "Empresas", mostra no plural apenas para ROOT, singular para os demais
   */
  getItemLabel(label: string): string {
    if (label === 'Empresas') {
      // ROOT atuando como ADMIN mostra "Empresa" (singular), como faria um ADMIN
      if (this.permission === 'ROOT' && this.rootHasActiveClinic) return 'Empresa';
      return this.permission === 'ROOT' ? 'Empresas' : 'Empresa';
    }
    return label;
  }

  /**
   * Retorna a rota correta para o item do menu
   * Para "Empresas" quando não é ROOT, redireciona para a página de edição da própria empresa
   */
  getItemRoute(label: string, route: string): string {
    if (label === 'Empresas') {
      // ROOT atuando como ADMIN: redireciona para a clínica ativa
      if (this.permission === 'ROOT' && this.rootHasActiveClinic) {
        const activeClinic = this.contextService.getActiveClinicSnapshot();
        return activeClinic?.cnpj ? `/main/v2/company/${activeClinic.cnpj}/edit/info` : route;
      }
      // ADMIN normal: redireciona para a própria empresa
      if (this.permission !== 'ROOT') {
        const clinicCnpj = this.authService.getClinicCnpj();
        return clinicCnpj ? `/main/v2/company/${clinicCnpj}/edit/info` : route;
      }
    }
    return route;
  }

  /**
   * Verifica se uma rota está ativa.
   * Para rotas de módulos dinâmicos (/modules/{module}), usa match exato.
   * Para outras rotas, permite match de prefixo.
   */
  isRouteActive(route: string): boolean {
    const currentUrl = this.router.url.split('?')[0]; // Remove query params
    const itemRoute = route.split('?')[0];
    
    // Para rotas de módulos dinâmicos, verificar match exato
    // Rotas como /main/v2/modules/alunos não devem ativar quando estamos em /main/v2/modules/alunos/forms
    if (itemRoute.match(/\/main\/v2\/modules\/[^\/]+$/)) {
      // Rota base do módulo (ex: /main/v2/modules/alunos)
      // Deve ser ativa apenas se a URL atual é exatamente essa OU se é uma sub-página de entidade (ex: /alunos/{id}/edit)
      // Mas NÃO deve ser ativa se é /alunos/forms ou /alunos/signatures
      const moduleBase = itemRoute;
      if (currentUrl === moduleBase) return true;
      
      // Verifica se é uma página de entidade (ID, create, edit) mas não forms/signatures
      const afterModule = currentUrl.replace(moduleBase, '');
      if (afterModule && !afterModule.startsWith('/forms') && !afterModule.startsWith('/signatures')) {
        // É uma sub-página como /{id}, /{id}/edit, /create
        return currentUrl.startsWith(moduleBase + '/');
      }
      return false;
    }
    
    // Para rotas normais, usar match de prefixo
    return currentUrl.startsWith(itemRoute) || currentUrl === itemRoute;
  }

}
